import { Box, Grid, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import AmountField from "../../../../common/components/functional/AmountField";
import FormSection from "../../../../common/components/functional/formSection/FormSection";
import InfoTooltip from "../../../../common/components/functional/InfoTooltip/InfoTooltip";
import { TextFieldLight } from "../../../../common/components/styled/styled";
import { BasicInfoOrderFields } from "../../additionalInfo/BasicInfoOrderFields";

type Props = {
  isExpertAuthorizedToEdit: boolean;
  values: {
    workLocationName: string;
    workLocationDescriptionForEezy: string;
    amount: number;
    originalAmount?: number;
    createdAt: EpochTimeStamp;
    createdAtWithTimestamp?: EpochTimeStamp;
    title: string;
    orderer: string;
    customerOrgName: string;
    orderTemplateName: string;
  };
  isOrderFulfilled: boolean;
  handleAmountChange: (amount: number) => void;
  assignedTalentsAmount: number;
};

export const BasicInfo = ({
  isExpertAuthorizedToEdit,
  values,
  isOrderFulfilled,
  handleAmountChange,
  assignedTalentsAmount,
}: Props) => {
  const { t } = useTranslation("order");
  const showTooltip = values.workLocationDescriptionForEezy.length > 0;
  const [originalAmount] = useState<number>(values.amount);
  const showOriginalAmountInfo =
    values.originalAmount !== undefined &&
    values.originalAmount !== values.amount;

  return (
    <FormSection title={t("orderDetails")}>
      <BasicInfoOrderFields value={values} isNewOrder={false} />
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary">{t("workLocation")}</Typography>
      </Grid>
      <Grid item container xs={9}>
        <TextFieldLight
          sx={{ width: "360px" }}
          value={values.workLocationName}
          size="small"
          name="workLocation"
          disabled
        />
        {showTooltip && (
          <Box display="flex" alignItems="center" marginLeft="13px">
            <InfoTooltip title={values.workLocationDescriptionForEezy} />
          </Box>
        )}
      </Grid>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary">{t("orderTemplate")}</Typography>
      </Grid>
      <Grid item xs={9}>
        <TextFieldLight
          sx={{ width: "360px" }}
          value={values.orderTemplateName}
          size="small"
          name="orderTemplate"
          disabled
        />
      </Grid>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary">{t("amount")}</Typography>
      </Grid>
      <Grid item xs={9}>
        <Box display="flex" alignItems="center" gap="8px">
          <AmountField
            disabled={isOrderFulfilled || !isExpertAuthorizedToEdit}
            sx={{ width: "140px" }}
            size="small"
            value={values.amount}
            onChange={(value: number) => handleAmountChange(value)}
            min={assignedTalentsAmount || 1}
            max={originalAmount}
          />
          {showOriginalAmountInfo ? (
            <InfoTooltip
              title={t("originalOrderedAmountInfo", {
                count: values.originalAmount,
              })}
            />
          ) : null}
        </Box>
      </Grid>
      <Grid container item xs={3} justifyContent="flex-end" alignItems="center">
        <Typography variant="secondary">{t("fulfillmentRate")}</Typography>
      </Grid>
      <Grid item xs={9}>
        <Box display="flex" justifyContent="center" width="140px">
          <Typography variant="secondary">
            {`${assignedTalentsAmount}/${values.amount}`}
          </Typography>
        </Box>
      </Grid>
    </FormSection>
  );
};
